// How an authority's extra report fields (Authority.requiredFields) are asked
// for on the QR report page. Mirrors the app's
// lib/features/create/report_field_spec.dart: the backend only sends plain
// labels, structured fields are recognised by label, and anything unknown
// falls back to a text box. Keep the option lists byte-identical to the app —
// the ERP browser extension matches them exactly.

export type ReportFieldType = 'text' | 'category' | 'availabilitySlot' | 'autoInjected';

export interface ReportFieldSpec {
	type: ReportFieldType;
	hint: string;
	helper?: string;
	options?: string[];
}

export const AVAILABILITY_SLOTS = ['9 AM – 5:30 PM', '9 AM – 1 PM', '2 PM – 5:30 PM'];

export const COMPLAINT_CATEGORIES = [
	'Electrical',
	'Civil Mason',
	'Civil Carpentary',
	'Civil Sewer',
	'Civil Plumber',
	'Civil Welding',
	'Civil Glazing',
	'Civil Others',
	'Civil Painter/Polisher',
	'Horticulture',
	'Air Conditioning and Cold Rooms',
	'Other',
];

/** Mobile/contact/phone fields are filled from the reporter's account, never typed. */
export const isAutoInjectedField = (label: string) => {
	const l = label.trim().toLowerCase();
	return l.includes('mobile') || l.includes('contact') || l.includes('phone');
};

/**
 * The student's entry number. Its value is remembered on the account
 * (Users.entryNumber) and prefilled on the next report, still editable. Same
 * "contains 'entry'" rule as the app and the backend.
 */
export const isEntryNumberField = (label: string) => label.trim().toLowerCase().includes('entry');

/** The form the backend stores an entry number in: uppercase alphanumerics, max 32. */
export const normaliseEntryNumber = (value: string) =>
	value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 32);

export const specForReportField = (label: string): ReportFieldSpec => {
	const raw = label.trim();
	const l = raw.toLowerCase();

	if (isAutoInjectedField(raw)) return { type: 'autoInjected', hint: '' };

	if (l === 'availability time') {
		return {
			type: 'availabilitySlot',
			hint: 'Select your availability',
			helper: 'When can the worker visit your room?',
			options: AVAILABILITY_SLOTS,
		};
	}

	// startsWith also matches the legacy "Complaint Category: Electrical/…" label.
	if (l.startsWith('complaint category')) {
		return {
			type: 'category',
			hint: 'Select a category',
			helper: 'Pick the closest match. Choose "Other" if unsure.',
			options: COMPLAINT_CATEGORIES,
		};
	}

	let hint = `Enter ${raw.toLowerCase()}`;
	if (isEntryNumberField(raw)) hint = 'e.g. 2021CS10123';
	else if (l.includes('room')) hint = 'e.g. WD-5/NA washroom/TT room/etc';
	return { type: 'text', hint };
};

/** Heading for a field: the template label, with the legacy category hint trimmed off. */
export const prettyFieldLabel = (label: string) =>
	label.trim().toLowerCase().startsWith('complaint category') ? 'Complaint Category' : label.trim();

/**
 * The values sent as requiredFieldValues, matching what the app sends:
 * phone-type fields get the reporter's own number, and for "<Hostel>
 * Caretaker" authorities the location gets ", <Hostel> Hostel" appended so the
 * caretaker can paste it straight into the ERP.
 */
export const buildRequiredFieldValues = (
	fields: string[],
	typed: Record<string, string>,
	reporterPhone: string,
	authorityName: string
): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const field of fields) {
		if (isAutoInjectedField(field)) {
			if (reporterPhone) out[field] = reporterPhone;
		} else {
			out[field] = (typed[field] ?? '').trim();
		}
	}

	const caretakerAt = authorityName.toLowerCase().indexOf('caretaker');
	if (caretakerAt > 0) {
		const hostel = authorityName.substring(0, caretakerAt).trim();
		const locationKey = 'Room No. or Location';
		if (hostel && out[locationKey]) out[locationKey] = `${out[locationKey]}, ${hostel} Hostel`;
	}
	return out;
};
